import { LEAD_GENERATED_BY, leadGeneratedByDefault } from '../shared/leadAttribution';
import { ref, push, set, update, remove, onValue, get, query, orderByChild, equalTo } from 'firebase/database';
import { recordOutreach, outreachKindForActivity } from './outreach';
import { database } from '../firebaseConfig';
import { createNotification } from './notificationService';
import { applyMerge } from '../pages/crm/merge';
import { NOTIFICATIONS_ENABLED } from '../config/notifications';
import { normalizeRole } from '../config/roles';
import { ownerOf, canSeeRecord, indexOps } from './recordAccess';
import { requestCalendarSync } from './googleSync';
import { manualizeSteps, campaignSaveProblems, campaignsToStart, canManuallyEnroll, triggerDecision, enrollmentsToStop, enrollmentsToPauseOnReply, pauseOnReplyPatch, resumePatch, isLeadReply, buildSchedule, entryDateOf, addDaysIso, attemptedContactPatch, unresponsiveDue, unresponsivePatch, followUpTaskFields, reengagePlan } from './campaignTriggers';
import { assignedAgentOf, campaignSenderFor, canMessageLead } from '../shared/leadPermissions';

// CRM v1. Everything lives under the crm/ root:
//   crm/contacts/{id}            contact record (name, email, phone, source, tags[], notes, consent flags)
//   crm/activity/{contactId}/{id} timeline entries
//   crm/templates/{id}           email builder templates (blocks[])
//   crm/campaigns/{id}           sequences (steps[] of { channel, delayDays, ... })
//   crm/enrollments/{id}         a contact enrolled in a campaign
// Nothing here sends anything. Sending is stubbed and gated behind NOTIFICATIONS_ENABLED (false).

const now = () => new Date().toISOString();
const listOf = (val) => Object.entries(val || {}).map(([id, v]) => ({ id, ...v }));

export const watchPath = (path, cb) => onValue(ref(database, path), (snap) => cb(listOf(snap.val())), () => cb([]));

// Firebase rejects undefined anywhere in a write; drop those keys (deeply) so saves never fail on leftovers.
export const stripUndefined = (v) => {
  if (Array.isArray(v)) return v.map((x) => (x === undefined ? null : stripUndefined(x)));
  if (v && typeof v === 'object' && Object.getPrototypeOf(v) === Object.prototype) return Object.fromEntries(Object.entries(v).filter(([, x]) => x !== undefined).map(([k, x]) => [k, stripUndefined(x)]));
  return v;
};
// Admins read a whole owned collection; others only their own records (createdBy), as the rules allow.
const ownedVal = async (path, uid, isAdmin) => {
  try {
    if (isAdmin) return (await get(ref(database, path))).val() || {};
    if (!uid) return {};
    return (await get(query(ref(database, path), orderByChild('createdBy'), equalTo(uid)))).val() || {};
  } catch (e) { console.warn(`Could not read ${path}`, e); return {}; }
};
export const saveRecord = async (path, id, rawData, uid) => {
  const data = stripUndefined(rawData);
  if (id) { await update(ref(database, `${path}/${id}`), { ...data, updatedAt: now() }); return id; }
  const r = push(ref(database, path));
  await set(r, { ...data, createdAt: now(), updatedAt: now(), createdBy: uid || null });
  return r.key;
};
export const deleteRecord = (path, id) => remove(ref(database, `${path}/${id}`));

// Logs a timeline entry. Notes (and calls/texts with notes) also update the contact's latestNote
// summary so the Contacts list can show it without loading every timeline.
export const logActivity = async (contactId, entry, uid) => {
  const at = now();
  const r = push(ref(database, `crm/activity/${contactId}`));
  await set(r, { ...entry, at, by: uid || null });
  const ok = outreachKindForActivity(entry);
  if (ok && uid) await recordOutreach(uid, ok, 'contact', { contactId, apptType: entry.apptType });
  if (isLeadReply(entry) && !entry.skipPause) await pauseCampaignsOnReply(contactId, entry.channel || (String(entry.type).includes('text') ? 'text' : 'email'), uid).catch(() => {});
  const nt = entry.type === 'note' ? entry.text : (entry.notes || '').trim() ? (entry.text || entry.notes) : '';
  if (nt) await update(ref(database, `crm/contacts/${contactId}`), { latestNote: { id: r.key, text: String(nt).slice(0, 300), at, type: entry.type } });
  return r.key;
};

// Finishes an earlier timeline entry (e.g. "Call started" becomes the logged call with its outcome) so one
// call is one entry: no duplicate log and no double outreach count.
export const updateActivity = async (contactId, activityId, patch) => {
  await update(ref(database, `crm/activity/${contactId}/${activityId}`), { ...patch, updatedAt: now() });
  const nt = (patch.notes || '').trim() ? (patch.text || patch.notes) : '';
  if (nt) await update(ref(database, `crm/contacts/${contactId}`), { latestNote: { id: activityId, text: String(nt).slice(0, 300), at: now(), type: patch.type || 'call' } });
  return activityId;
};
// Standard fields on a call entry: direction, number dialed and the agent's name (the uid is saved as `by`).
export const callFields = (phone, user, extra = {}) => ({
  direction: 'outbound',
  number: String(phone || '').replace(/[^0-9+]/g, '') || null,
  agentName: (user && (user.displayName || user.email)) || '',
  ...extra,
});

// Recomputes a contact's note summaries from its timeline: pinnedNote = newest pinned entry,
// latestNote = newest note-like entry (notes, calls/texts with notes), else the contact's notes field.
// Writes them back only when write is true (editable contacts). Returns { latestNote, pinnedNote }.
const noteText = (a) => (a.type === 'note' ? a.text : (a.notes || '').trim() ? (a.text || a.notes) : '');
export const computeNoteSummary = async (contact, write = false) => {
  const snap = await get(ref(database, `crm/activity/${contact.id}`));
  let latest = null; let pinned = null;
  snap.forEach((ch) => {
    const a = ch.val() || {};
    const at = a.at || 0;
    const t = noteText(a);
    if (t && (!latest || at > latest.at)) latest = { id: ch.key, text: String(t).slice(0, 300), at, type: a.type };
    if (a.pinned && (!pinned || (a.pinnedAt || at) > (pinned.pinnedAt || 0))) pinned = { id: ch.key, text: String(a.text || a.notes || '').slice(0, 300), at, type: a.type || '', pinnedAt: a.pinnedAt || at };
  });
  if (!latest && (contact.notes || '').trim()) latest = { id: null, text: String(contact.notes).trim().slice(0, 300), at: contact.createdAt || 0, type: 'note' };
  const out = { latestNote: latest, pinnedNote: pinned };
  if (write) await update(ref(database, `crm/contacts/${contact.id}`), out);
  return out;
};
export const backfillLatestNote = (contact) => computeNoteSummary(contact, true);

// Email templates. Team (master) templates: scope 'team' or legacy records with no scope. Only admins
// change them. Agents edit by saving a personal copy (scope 'personal', ownerId, sourceTemplateId).
export const isTeamTemplate = (t) => !!t && (t.scope || 'team') === 'team';
// scope: 'team' (legacy/admin, everyone sees, admins edit) | 'shared' (agent's template shared with all agents; owner or admin edits)
// | 'personal' (private: only the owner ever sees it, admins included).
export const isSharedTemplate = (t) => !!t && t.scope === 'shared';
export const visibleTemplates = (all, uid) => (all || []).filter((t) => isTeamTemplate(t) || isSharedTemplate(t) || (t.scope === 'personal' && !!uid && t.ownerId === uid));
export const canEditTemplate = (t, uid, isAdmin) => (isTeamTemplate(t) ? !!isAdmin : isSharedTemplate(t) ? (t.ownerId === uid || !!isAdmin) : t.ownerId === uid);
const scopeFor = (vis, isAdmin) => (vis === 'shared' ? (isAdmin ? 'team' : 'shared') : 'personal');
const tplData = (tpl) => { const { id, createdAt, updatedAt, createdBy, from, starterId, visibility, ...data } = tpl; return data; };
// mode: 'update' (edit tpl in place, permission-checked) | 'copy' (new personal copy named `name`)
// | 'new' (brand-new: team when admin, else personal).
export const saveTemplate = async (tpl, mode, name, uid, isAdmin, source = null) => {
  const data = { ...tplData(tpl), name: (name || tpl.name || '').trim() };
  if (mode === 'update') {
    if (!canEditTemplate(tpl, uid, isAdmin)) throw new CrmPermissionError('Team templates can only be changed by an admin. Save a copy instead.');
    if (tpl.visibility && tpl.ownerId === uid && !isTeamTemplate(tpl)) data.scope = tpl.visibility === 'shared' ? 'shared' : 'personal';
    return saveRecord('crm/templates', tpl.id, data, uid);
  }
  if (mode === 'copy') {
    return saveRecord('crm/templates', null, { ...data, scope: tpl.visibility === 'shared' ? 'shared' : 'personal', ownerId: uid, sourceTemplateId: source?.id || tpl.sourceTemplateId || null }, uid);
  }
  const scope = tpl.visibility ? scopeFor(tpl.visibility, isAdmin) : (isAdmin ? 'team' : 'personal');
  return saveRecord('crm/templates', null, { ...data, scope, ownerId: scope === 'team' ? null : uid }, uid);
};

// 'text' is not offered: campaign texts are manual agent tasks (Text quick action). Old text steps convert at enroll.
export const CHANNELS = ['email', 'video', 'task', 'notify'];
export const CHANNEL_LABEL = { email: 'Email', text: 'Text (converted to agent task)', video: 'Video', task: 'Agent task', notify: 'Agent alert (in-app)' };

// Opt-outs. New fields: optOutCalls, optOutTexts, dnc. Older records may carry smsOptOut / callOptOut.
// Do Not Call blocks calls AND texts (Kelly's rule). Email still allowed unless email_opt_out.
export const optedOutTexts = (c) => !!(c?.optOutTexts || c?.smsOptOut || c?.dnc);
export const optedOutCalls = (c) => !!(c?.optOutCalls || c?.callOptOut);
export const noCalls = (c) => optedOutCalls(c) || !!c?.dnc;

// Can this step reach this contact? Texts skip anyone opted out of texts; emails respect unsubscribe.
export const canReach = (contact, step) => {
  if (contact && contact.archived) return { ok: false, reason: 'archived' };
  // Internal alerts to the assigned agent (not the lead) skip the lead's consent and opt-outs.
  // Manual text tasks still honor the lead's text rules (DNC, no phone, opted out, paused).
  if (step && step.manualText) { if (isPaused(contact)) return { ok: false, reason: 'paused' }; if (contact.dnc) return { ok: false, reason: 'dnc' }; if (!contact.phone) return { ok: false, reason: 'no_phone' }; if (optedOutTexts(contact)) return { ok: false, reason: 'text_opt_out' }; }
  if (step && step.to === 'assignedAgent') return { ok: true };
  // "Not ready" (paused) contacts skip texts and agent tasks until their date; emails still go.
  if ((step.channel === 'text' || step.channel === 'task') && isPaused(contact)) return { ok: false, reason: 'paused' };
  if (step.channel === 'text') {
    if (contact.dnc) return { ok: false, reason: 'dnc' };
    if (!contact.phone) return { ok: false, reason: 'no_phone' };
    return optedOutTexts(contact) ? { ok: false, reason: 'text_opt_out' } : { ok: true };
  }
  if (step.channel === 'email' || step.channel === 'video') {
    if (contact.email_opt_out) return { ok: false, reason: 'email_opt_out' };
    if (!contact.email) return { ok: false, reason: 'no_email' };
  }
  return { ok: true };
};

// Stubbed sender. Never sends while NOTIFICATIONS_ENABLED is false; only reports what would happen.
// When sending is built, campaign/auto emails go out as the ASSIGNED agent (campaignSenderFor).
export const runStepStub = (contact, step, users = null) => {
  const reach = canReach(contact, step);
  if (!reach.ok) return { sent: false, reason: reach.reason };
  const sender = users ? campaignSenderFor(contact, users) : null;
  if (!NOTIFICATIONS_ENABLED) return { sent: false, reason: 'sending_disabled', sender };
  return { sent: false, reason: 'sender_not_built', sender };
};

export const enrollContacts = async (campaignIn, contacts, uid, { startToday = false, onlySteps = null, reason = null, isAdmin = false } = {}) => {
  const campaign = { ...campaignIn, steps: manualizeSteps(campaignIn.steps) };
  const today = isoDay(new Date());
  // Archived contacts are never enrolled; agent campaigns only take leads assigned to the campaign's owner.
  // Imported BoldTrail leads start from their original entry date (past steps skipped), not as brand-new leads.
  // Non-admins only enroll leads they are assigned to, or own because they entered them (Kelly, Nov 3).
  for (const c of contacts.filter((x) => canManuallyEnroll(campaign, x) && canMessageLead(x, uid, isAdmin))) {
    let schedule = buildSchedule(campaign.steps || [], c, today, canReach, { startToday });
    // Re-engage restarts only the agent follow-up steps; the plan's Day 1 becomes today.
    if (onlySteps) { const keep = new Set(onlySteps); const off = Math.min(...onlySteps.map((i) => Number(campaign.steps[i].delayDays) || 0));
      schedule = schedule.map((x) => (keep.has(x.index) ? { ...x, dueDate: addDaysIso(today, (Number(campaign.steps[x.index].delayDays) || 0) - off) } : { ...x, status: 'skipped', reason: 'not_in_restart' })); }
    await sendAgentAlerts(campaign, c, schedule, uid).catch((e) => console.warn('Could not create agent alert', e));
    const startDate = (!startToday && entryDateOf(c)) || today;
    const enrId = await saveRecord('crm/enrollments', null, { campaignId: campaign.id, contactId: c.id, status: 'active', startDate, schedule, ...(reason ? { reason } : {}) }, uid);
    const made = await createFollowUpTasks(campaign, c, schedule, enrId, uid).catch((e) => { console.warn('Could not create follow-up tasks', e); return 0; });
    await logActivity(c.id, { type: 'enrolled', text: `Enrolled in campaign "${campaign.name}"${startDate < today ? ` (from entry date ${startDate})` : ''}${made ? `, ${made} agent follow-up tasks` : ''}` }, uid);
  }
};

// Agent new-lead alert: in-app notification (users/<agent>/notifications), never an SMS. Only rows due today or
// earlier-but-not-skipped fire, so the entry-date skip logic applies (an old import gets no alert).
export const sendAgentAlerts = async (campaign, contact, schedule, uid, notify = createNotification) => {
  const agent = assignedAgentOf(contact, uid);
  if (!agent) return 0;
  let n = 0;
  for (const row of schedule) {
    const step = (campaign.steps || [])[row.index];
    if (!step || step.channel !== 'notify' || row.status !== 'scheduled') continue;
    await notify(agent, { type: 'new_lead', title: step.name, message: applyMerge(step.message || '', contact, {}), crmContactId: contact.id || null, campaignId: campaign.id || null, dueDate: row.dueDate, channel: 'in_app' });
    row.status = 'notified';
    n += 1;
  }
  return n;
};

// Agent follow-up (12 Day Plan): each scheduled 'task' step becomes a real task for the assigned agent,
// with its quick action (call/text) and script so the call window can show it.
export const createFollowUpTasks = async (campaign, contact, schedule, enrollmentId, uid) => {
  const agent = assignedAgentOf(contact, uid);
  if (!agent) return 0;
  const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.name || 'lead';
  let n = 0;
  for (const row of schedule) {
    const step = (campaign.steps || [])[row.index];
    if (!step || step.channel !== 'task' || row.status !== 'scheduled') continue;
    const r = push(ref(database, `users/${agent}/tasks`));
    await set(r, {
      name: `${step.name}: ${name}`, title: `${step.name}: ${name}`, description: step.script || step.message || '',
      role: 'Agent', type: 'Follow-up', appliesTo: 'All', mode: 'task', source: 'crm-campaign', campaignId: campaign.id || null, enrollmentId: enrollmentId || null, stepIndex: row.index,
      ...followUpTaskFields(step), crmContactId: contact.id, dueDate: row.dueDate, status: 'pending', assignee: agent,
      createdAt: now(), updatedAt: now(), createdBy: uid || null, completedAt: null, notes: '',
    });
    n += 1;
  }
  return n;
};

// Auto #attemptedcontact when an agent calls or texts the lead from Resi. Never throws.
export const markAttemptedContact = async (contact) => {
  try { const p = attemptedContactPatch(contact); if (p) await update(ref(database, `crm/contacts/${contact.id}`), { ...p, updatedAt: now() }); return !!p; } catch (e) { console.warn('Could not tag #attemptedcontact', e); return false; }
};

// Ship 2: restart the agent 12 Day Plan for leads BoldTrail shows active again (flag set by the server). Never throws.
export const applyReengage = async (contacts, uid, isAdmin) => {
  const pending = (contacts || []).filter((c) => c && c.reengagePending);
  if (!pending.length) return 0;
  try {
    const campaigns = await triggerCampaigns(uid, isAdmin);
    const enr = Object.values(await ownedVal('crm/enrollments', uid, isAdmin));
    let n = 0;
    for (const c of pending) {
      const plan = reengagePlan(c, campaigns, enr);
      if (plan.ok) {
        await enrollContacts(plan.campaign, [c], uid, { startToday: true, onlySteps: plan.stepIndexes, reason: 'boldtrail_activity' });
        n += 1;
      }
      await update(ref(database, `crm/contacts/${c.id}`), { reengagePending: null, ...(plan.ok ? { reengagedAt: now(), tags: (c.tags || []).filter((t) => !/^#*unresponsive$/i.test(String(t).trim())) } : {}), updatedAt: now() });
      if (!plan.ok) await logActivity(c.id, { type: 'status', text: `BoldTrail activity noted; follow-up not restarted (${plan.reason}).` }, uid);
    }
    return n;
  } catch (e) { console.warn('Re-engage failed', e); return 0; }
};

// Day 12 sweep: for the user's visible enrollments, tag #unresponsive when no contact was made by Day 12.
// Runs client-side when an agent opens their Lead Tasks (no server cron in ship 1). Never throws.
export const sweepUnresponsive = async (contacts, uid, isAdmin) => {
  try {
    const today = isoDay(new Date());
    const enr = await ownedVal('crm/enrollments', uid, isAdmin);
    const byId = Object.fromEntries((contacts || []).map((c) => [c.id, c]));
    let n = 0;
    for (const [eid, e] of Object.entries(enr || {})) {
      const c = e && byId[e.contactId];
      if (!c || e.unresponsiveChecked) continue;
      const act = Object.values((await get(ref(database, `crm/activity/${c.id}`))).val() || {});
      if (unresponsiveDue(c, e, act, today)) {
        const p = unresponsivePatch(c);
        if (p) { await update(ref(database, `crm/contacts/${c.id}`), { ...p, updatedAt: now() }); await logActivity(c.id, { type: 'status', text: 'Day 12: no contact made. Tagged #unresponsive.' }, uid); n += 1; }
        await update(ref(database, `crm/enrollments/${eid}`), { unresponsiveChecked: today });
      }
    }
    return n;
  } catch (e) { console.warn('Unresponsive sweep failed', e); return 0; }
};

// Campaign save with Kelly's permission rules (team campaign: admin only). The database rules enforce the same.
export const saveCampaignChecked = async (existing, data, uid, isAdmin) => {
  const problems = campaignSaveProblems(data, existing, uid, isAdmin);
  if (problems.length) throw new CrmPermissionError(problems[0]);
  const clean = { ...data }; if (!isAdmin) clean.scope = 'agent';
  return saveRecord('crm/campaigns', existing ? existing.id : null, clean, uid);
};

// Campaigns this user can see for auto-start: their own plus team campaigns (admins/MM read all).
const triggerCampaigns = async (uid, isAdmin) => {
  const own = await ownedVal('crm/campaigns', uid, isAdmin);
  let team = {};
  if (!isAdmin) { try { team = (await get(query(ref(database, 'crm/campaigns'), orderByChild('scope'), equalTo('team')))).val() || {}; } catch (e) { team = {}; } }
  return Object.entries({ ...team, ...own }).map(([id, v]) => ({ id, ...v }));
};
// Auto-start matching campaigns for a contact (new lead, import, or tags added). Never throws: a lead save
// must not fail because a campaign could not start. Sending itself stays off until NOTIFICATIONS_ENABLED.
export const autoStartCampaigns = async (contact, { isNewLead = false } = {}, uid, isAdmin) => {
  try {
    if (!contact || !contact.id) return [];
    const campaigns = await triggerCampaigns(uid, isAdmin);
    const enrMap = await ownedVal('crm/enrollments', uid, isAdmin);
    const enr = Object.values(enrMap);
    // Kingston leads must not stay in Team Buyer Welcome (or any campaign that excludes their tags).
    for (const id of enrollmentsToStop(contact, campaigns, enrMap)) {
      const e = enrMap[id];
      const schedule = (e.schedule || []).map((x) => (x.status === 'scheduled' ? { ...x, status: 'skipped', reason: 'excluded_tag' } : x));
      await update(ref(database, `crm/enrollments/${id}`), { status: 'stopped_excluded', stoppedAt: now(), schedule });
      const cp = campaigns.find((x) => x.id === e.campaignId);
      logActivity(contact.id, { type: 'status', text: `Stopped "${cp ? cp.name : 'campaign'}": lead is in an exclusive campaign (e.g. Kingston).` }, uid).catch(() => {});
    }
    const start = campaignsToStart(contact, campaigns, { isNewLead, enrollments: enr });
    for (const cp of start) await enrollContacts(cp, [contact], uid, { isAdmin });
    campaigns.filter((cp) => cp.active && triggerDecision(cp, contact, { isNewLead, enrollments: enr }).reason === 'started_in_boldtrail')
      .forEach((cp) => logActivity(contact.id, { type: 'status', text: `Not enrolled in "${cp.name}": already started this sequence in BoldTrail.` }, uid).catch(() => {}));
    return start.map((cp) => cp.id);
  } catch (e) { console.warn('Could not auto-start campaigns', e); return []; }
};

// Lists: crm/lists/{id} = { name, kind: 'static' | 'smart', contactIds: [], tags: [], matchAll: bool }
// Smart lists match contacts by tag (any tag, or all tags when matchAll is true).
export const listMembers = (list, contacts) => {
  if (!list) return [];
  if (list.kind === 'smart') {
    const tags = list.tags || [];
    if (!tags.length) return [];
    return contacts.filter((c) => {
      const ct = c.tags || [];
      return list.matchAll ? tags.every((t) => ct.includes(t)) : tags.some((t) => ct.includes(t));
    });
  }
  const ids = list.contactIds || [];
  return contacts.filter((c) => ids.includes(c.id));
};

export const DISPOSITIONS = ['Asked not to call', 'No answer', 'Left VM', 'Bad number', 'Appointment set', 'Not ready', 'Call back'];

// ---- Contact access: private by default (see recordAccess.js) ----
// One owner (ownerUid) plus sharedWith { uid: true }. Owner, shared people and admins can see and work
// a contact; only the owner or an admin can share, transfer or delete it. Enforced by the database
// rules too (crm/contacts/$id). Legacy fields (ownerType/ownerId/assignedToId) are kept in step.
export const isTeamOwned = () => false;
export const canEditContact = (c, uid, isAdmin) => canSeeRecord(c, uid, isAdmin);
export const canViewContact = (c, uid, isAdmin) => canSeeRecord(c, uid, isAdmin);
export const contactOwner = ownerOf;

// New contacts belong to the agent they are for (an admin adding one for an agent), else the creator.
export const defaultOwnership = (uid, isAdmin, forAgentId) => {
  const owner = (isAdmin && forAgentId) || uid || '';
  return { ownerUid: owner, ownerType: 'agent', ownerId: owner, assignedToId: owner };
};

export class CrmPermissionError extends Error {}
export const saveContactChecked = async (existing, data, uid, isAdmin) => {
  if (existing && !canEditContact(existing, uid, isAdmin)) throw new CrmPermissionError('View only: you cannot edit this contact.');
  if (existing) {
    // Ownership and sharing only change through Share / Transfer.
    const { ownerUid, sharedWith, ownerType, ownerId, ...rest } = data;
    if (!isAdmin) delete rest.leadGeneratedBy; // only admins change "Lead generated by"
    const id = await saveRecord('crm/contacts', existing.id, rest, uid);
    const before = new Set((existing.tags || []).map((t) => String(t).toLowerCase()));
    if ((rest.tags || []).some((t) => !before.has(String(t).toLowerCase()))) await autoStartCampaigns({ ...existing, ...rest, id }, { isNewLead: false }, uid, isAdmin);
    return id;
  }
  const own = data.ownerUid ? { ownerUid: data.ownerUid } : defaultOwnership(uid, isAdmin, data.assignedToId);
  const leadGeneratedBy = isAdmin && LEAD_GENERATED_BY.includes(data.leadGeneratedBy) ? data.leadGeneratedBy : leadGeneratedByDefault({ creatorUid: uid, ownerUid: own.ownerUid, imported: !!data.imported });
  const rec = stripUndefined({ ...data, ...own, leadGeneratedBy, ownerId: own.ownerUid, ownerType: 'agent', createdAt: now(), updatedAt: now(), createdBy: uid || null });
  const r = push(ref(database, 'crm/contacts'));
  await update(ref(database), { [`crm/contacts/${r.key}`]: rec, ...indexOps('contacts', r.key, rec) });
  await autoStartCampaigns({ ...rec, id: r.key }, { isNewLead: true }, uid, isAdmin);
  return r.key;
};
export const updateContactChecked = async (existing, patch, uid, isAdmin) => {
  if (!canEditContact(existing, uid, isAdmin)) throw new CrmPermissionError('View only: you cannot edit this contact.');
  await update(ref(database, `crm/contacts/${existing.id}`), { ...patch, updatedAt: now() });
};
export const logActivityChecked = (existing, entry, uid, isAdmin) => {
  if (!canEditContact(existing, uid, isAdmin)) throw new CrmPermissionError('View only: you cannot edit this contact.');
  return logActivity(existing.id, entry, uid);
};

// ---- Opt-outs ----
// recordOptOut sets the flag, stamps when and where it came from, and logs it on the contact's timeline.
export const OPT_OUT_FIELD = { calls: 'optOutCalls', texts: 'optOutTexts', email: 'email_opt_out', dnc: 'dnc' };
const OPT_OUT_TEXT = { calls: 'Opted out of phone calls', texts: 'Opted out of text messages', email: 'Unsubscribed from marketing email', dnc: 'Added to Do Not Call list' };
export const recordOptOut = async (contactId, type, source = 'manual', note = '', uid = null) => {
  const field = OPT_OUT_FIELD[type];
  if (!contactId || !field) throw new Error(`Unknown opt-out type: ${type}`);
  const at = now();
  const patch = { [field]: true, [`optOut/${type}/at`]: at, [`optOut/${type}/source`]: source, [`optOut/${type}/note`]: note || '', updatedAt: at };
  let removed = [];
  if (type === 'dnc') {
    // Kelly's rule: Do Not Call removes the phone number(s) from the record. They are kept (not shown) in
    // optOut/dnc/removedPhones so a re-added number stays blocked and duplicate checks still match.
    const c = (await get(ref(database, `crm/contacts/${contactId}`))).val() || {};
    removed = [c.phone, c.phone2, ...(c.additionalPhones || [])].map((p) => String(p || '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '')).filter((p) => p.length >= 10);
    const prev = (c.optOut && c.optOut.dnc && c.optOut.dnc.removedPhones) || [];
    Object.assign(patch, { phone: '', phone2: null, additionalPhones: null, 'optOut/dnc/removedPhones': [...new Set([...prev, ...removed])] });
  }
  await update(ref(database, `crm/contacts/${contactId}`), patch);
  await logActivity(contactId, { type: 'opt_out', optOut: type, source, text: `${OPT_OUT_TEXT[type]} (${source})${note ? `. ${note}` : ''}` }, uid);
  for (const p of removed) await logActivity(contactId, { type: 'dnc_phone_removed', text: `Phone removed due to Do Not Call: ${formatPhoneDash(p)}` }, uid);
};
// Take a contact off Do Not Call (only through the confirm dialog). Optionally restores one removed number.
export const removeFromDnc = async (contact, { restorePhone = '', reason = '' } = {}, uid = null, isAdmin = false) => {
  if (!canEditContact(contact, uid, isAdmin)) throw new CrmPermissionError('View only: you cannot edit this contact.');
  const at = now(); const left = ((contact.optOut && contact.optOut.dnc && contact.optOut.dnc.removedPhones) || []).filter((p) => p !== restorePhone);
  await update(ref(database, `crm/contacts/${contact.id}`), { dnc: false, dncFlag: null, 'optOut/dnc': null, ...(left.length ? { 'optOut/dncHistory/removedPhones': left } : {}), ...(restorePhone ? { phone: restorePhone } : {}), updatedAt: at });
  await logActivity(contact.id, { type: 'opt_in', optOut: 'dnc', text: `Removed from Do Not Call list${restorePhone ? `; phone restored: ${formatPhoneDash(restorePhone)}` : ''}${reason ? `. Reason: ${reason}` : ''}` }, uid);
};
// True when this number was removed from some contact because of Do Not Call.
export const isDncRemovedPhone = (contacts, phone) => { const d = String(phone || '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, ''); return d.length >= 10 && (contacts || []).some((c) => c && c.dnc && ((c.optOut && c.optOut.dnc && c.optOut.dnc.removedPhones) || []).includes(d)); };

// "Asked not to call": opt out of calls AND add to Do Not Call, each logged with date/source.
export const recordAskedNotToCall = async (contactId, source, note = '', uid = null) => {
  await recordOptOut(contactId, 'calls', source, note, uid);
  await recordOptOut(contactId, 'dnc', source, note, uid);
};

// "Not ready": pick when to re-engage. Saves reengageAt + leadStatus on the contact,
// logs it, and (unless Never) creates a follow-up task for the assigned agent (or owner, or the
// person logging) in their task list (users/{uid}/tasks), due on that date.
// Pause state, computed on read so it lifts by itself once reengageAt arrives.
const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
export const pauseInfo = (c) => {
  if (!c) return null;
  if (c.reengageLabel === 'Never' || c.leadStatus === 'Not interested') return { label: 'Not interested', until: null };
  if (c.reengageAt && c.reengageAt > todayIso()) return { label: `Not ready - paused until ${new Date(`${c.reengageAt}T12:00:00`).toLocaleDateString()}`, until: c.reengageAt };
  return null;
};
export const isPaused = (c) => !!pauseInfo(c);
export const isNotInterested = (d) => /^(not ready|not interested)/i.test(d || '');
export const REENGAGE_OPTIONS = [['in 1 month', 1], ['in 3 months', 3], ['in 6 months', 6], ['in 1 year', 12], ['in 18 months', 18], ['in 2 years', 24], ['Never', null]];
const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export const setReengage = async (contact, label, months, uid, isAdmin) => {
  if (!canEditContact(contact, uid, isAdmin)) throw new CrmPermissionError('View only: you cannot edit this contact.');
  const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.name || 'this contact';
  if (months == null) {
    await update(ref(database, `crm/contacts/${contact.id}`), { leadStatus: 'Not interested', reengageAt: null, reengageLabel: 'Never', updatedAt: now() });
    await logActivity(contact.id, { type: 'status', text: 'Not interested. Re-engage: never' }, uid);
    return null;
  }
  let due;
  if (typeof months === 'string') due = months; // picked date YYYY-MM-DD
  else { const d = new Date(); d.setMonth(d.getMonth() + months); due = isoDay(d); }
  const agent = assignedAgentOf(contact, uid);
  let taskId = null;
  if (agent) {
    const r = push(ref(database, `users/${agent}/tasks`));
    taskId = r.key;
    await set(r, {
      name: `Re-engage ${name}`, title: `Re-engage ${name}`,
      description: `Was not ready (${label}). Time to reach back out.${contact.phone ? ` Phone: ${formatPhoneDash(contact.phone)}.` : ''}`,
      role: 'Agent', type: 'Follow-up', appliesTo: 'All', mode: 'task', source: 'crm-reengage',
      crmContactId: contact.id, dueDate: due, status: 'pending', assignee: agent,
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), createdBy: uid || null, completedAt: null, notes: '',
    });
  }
  await update(ref(database, `crm/contacts/${contact.id}`), { leadStatus: `Not ready - paused until ${due}`, reengageAt: due, reengageLabel: label, reengageTaskId: taskId, reengageTaskOwner: agent || null, updatedAt: now() });
  await logActivity(contact.id, { type: 'status', text: `Not ready. Re-engage ${label} (${due})${taskId ? ', follow-up task created' : ''}` }, uid);
  return due;
};

// "Call back": schedule a call-back task (users/{uid}/tasks) for the agent who logged it
// (falls back to the assignee), due at a date + time. Stores callbackAt (ISO) on the contact.
export const setCallback = async (contact, when, uid, isAdmin) => {
  if (!canEditContact(contact, uid, isAdmin)) throw new CrmPermissionError('View only: you cannot edit this contact.');
  const d = new Date(when);
  const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.name || 'this contact';
  const agent = uid || contact.assignedToId;
  const label = d.toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  let taskId = null;
  if (agent) {
    const r = push(ref(database, `users/${agent}/tasks`));
    taskId = r.key;
    await set(r, {
      name: `Call back ${name}`, title: `Call back ${name}`,
      description: `Call back scheduled for ${label}.${contact.phone ? ` Phone: ${formatPhoneDash(contact.phone)}.` : ''}`,
      role: 'Agent', type: 'Follow-up', appliesTo: 'All', mode: 'task', source: 'crm-callback',
      crmContactId: contact.id, dueDate: isoDay(d), dueTime: `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`, dueAt: d.toISOString(),
      status: 'pending', assignee: agent, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), createdBy: uid || null, completedAt: null, notes: '',
    });
  }
  await update(ref(database, `crm/contacts/${contact.id}`), { callbackAt: d.toISOString(), callbackTaskId: taskId, callbackTaskOwner: agent || null, updatedAt: now() });
  await logActivity(contact.id, { type: 'status', text: `Call back scheduled ${label}${taskId ? ', task created' : ''}` }, uid);
  return d.toISOString();
};
export const callbackText = (c) => (c?.callbackAt && new Date(c.callbackAt) > new Date()
  ? `Call back scheduled ${new Date(c.callbackAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : '');

// Appointments (crm/appointments). Saving one also: adds a task for that day to the agent's task
// list, and queues reminder email + text 2 hours before in crm/scheduledMessages (status 'pending').
// Nothing sends those yet: it needs SendGrid/Twilio plus a scheduled Cloud Function that picks up
// pending messages whose sendAt has passed.
export const APPT_TYPES = ['Phone', 'Zoom', 'In person'];
export const apptLabels = (a) => {
  const d = new Date(a.startAt);
  return {
    date: d.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' }),
    time: d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
    type: a.type, location: a.location || '', zoomLink: a.zoomLink || '',
  };
};
export const saveAppointment = async (contact, appt, uid, isAdmin, reminders = { email: '', text: '', subject: '' }) => {
  if (!canEditContact(contact, uid, isAdmin)) throw new CrmPermissionError('View only: you cannot edit this contact.');
  const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.name || 'contact';
  const start = new Date(appt.startAt);
  const agent = uid || contact.assignedToId;
  const r = push(ref(database, 'crm/appointments'));
  const record = { contactId: contact.id, contactName: name, agentUid: agent || null, startAt: start.toISOString(), type: appt.type, location: appt.type === 'In person' ? (appt.location || '') : '', zoomLink: appt.type === 'Zoom' ? (appt.zoomLink || '') : '', notes: appt.notes || '', status: 'scheduled', createdAt: now(), createdBy: uid || null };
  await set(r, record);
  const L = apptLabels(record);
  let taskId = null;
  if (agent) {
    const t = push(ref(database, `users/${agent}/tasks`));
    taskId = t.key;
    await set(t, {
      name: `Appointment: ${name} (${appt.type})`, title: `Appointment: ${name} (${appt.type})`,
      description: `${L.date} at ${L.time}. ${appt.type}${record.location ? ` at ${record.location}` : ''}${record.zoomLink ? `. Zoom: ${record.zoomLink}` : ''}${contact.phone ? `. Phone: ${formatPhoneDash(contact.phone)}` : ''}`,
      role: 'Agent', type: 'Appointment', appliesTo: 'All', mode: 'task', source: 'crm-appointment', crmContactId: contact.id, appointmentId: r.key,
      dueDate: isoDay(start), dueTime: `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`, dueAt: start.toISOString(),
      status: 'pending', assignee: agent, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), createdBy: uid || null, completedAt: null, notes: '',
    });
  }
  // Reminders 2 hours before, skipping opted-out channels or past times.
  const sendAt = new Date(start.getTime() - 2 * 3600 * 1000);
  const queued = [];
  if (sendAt > new Date()) {
    if (contact.email && !contact.email_opt_out && reminders.email) queued.push({ channel: 'email', to: contact.email, subject: reminders.subject, body: reminders.email });
    // No automated texts (Kelly, Nov 2, 2026): the text reminder is not queued; the agent texts manually if wanted.
    for (const m of queued) await set(push(ref(database, 'crm/scheduledMessages')), { ...m, kind: 'appointment_reminder', appointmentId: r.key, contactId: contact.id, sendAt: sendAt.toISOString(), status: 'pending', createdAt: now(), createdBy: uid || null });
  }
  await update(ref(database, `crm/appointments/${r.key}`), { taskId, reminders: queued.map((m) => m.channel) });
  await update(ref(database, `crm/contacts/${contact.id}`), { nextAppointmentAt: start.toISOString(), nextAppointmentId: r.key, updatedAt: now() });
  requestCalendarSync();
  await logActivity(contact.id, { type: 'appointment', appointmentId: r.key, text: `Appointment set: ${L.date} at ${L.time} (${appt.type}${record.location ? `, ${record.location}` : ''})${queued.length ? `. Reminder ${queued.map((m) => m.channel).join(' + ')} queued for 2 hours before` : ''}` }, uid);
  return { id: r.key, ...record };
};
export const appointmentText = (c) => (c?.nextAppointmentAt && new Date(c.nextAppointmentAt) > new Date()
  ? `Appointment ${new Date(c.nextAppointmentAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : '');

// Pause a contact's active campaign enrollments because they replied. Logs one activity per paused campaign.
export const pauseCampaignsOnReply = async (contactId, channel = 'email', uid) => {
  if (!contactId) return [];
  const [cs, es] = await Promise.all([get(ref(database, 'crm/campaigns')), get(query(ref(database, 'crm/enrollments'), orderByChild('contactId'), equalTo(contactId)))]);
  const campaigns = Object.entries(cs.val() || {}).map(([id, v]) => ({ id, ...v }));
  const enr = es.val() || {};
  const at = now();
  const ids = enrollmentsToPauseOnReply(contactId, campaigns, enr);
  for (const id of ids) {
    await update(ref(database, `crm/enrollments/${id}`), pauseOnReplyPatch(enr[id], at, channel));
    const cp = campaigns.find((x) => x.id === enr[id].campaignId);
    await logActivity(contactId, { type: 'status', text: `Paused "${cp ? cp.name : 'campaign'}": lead replied by ${channel}. Remaining steps are on hold until someone resumes.` }, uid);
  }
  return ids;
};
export const resumeEnrollment = async (enrollment, uid) => {
  if (!enrollment?.id || enrollment.status !== 'paused_replied') return false;
  await update(ref(database, `crm/enrollments/${enrollment.id}`), resumePatch(enrollment, todayIso()));
  await logActivity(enrollment.contactId, { type: 'status', text: 'Campaign resumed after reply pause.' }, uid);
  return true;
};
const findContactsBy = async (pred) => Object.entries((await get(ref(database, 'crm/contacts'))).val() || {}).filter(([, c]) => pred(c)).map(([id]) => id);
// Stub for a future inbound-email (reply) webhook: pauses campaigns for the contact(s) with that email.
export const handleInboundEmail = async (fromEmail, subject = '') => {
  const em = String(fromEmail || '').trim().toLowerCase();
  if (!em) return { paused: [] };
  const ids = await findContactsBy((c) => String(c.email || '').trim().toLowerCase() === em);
  for (const id of ids) await logActivity(id, { type: 'email_reply', inbound: true, channel: 'email', text: `Lead replied by email${subject ? `: ${subject}` : ''}` }, null);
  return { contacts: ids };
};

// Stub for the future Twilio inbound-text webhook. STOP-type keywords opt the sender out of texts.
const STOP_WORDS = ['STOP', 'STOPALL', 'UNSUBSCRIBE', 'CANCEL', 'END', 'QUIT', 'OPTOUT', 'REVOKE'];
const digits = (p) => String(p || '').replace(/\D/g, '').slice(-10);
export const handleInboundText = async (fromPhone, body) => {
  const word = String(body || '').trim().toUpperCase().replace(/[^A-Z]/g, '');
  if (!STOP_WORDS.includes(word)) {
    // Any other reply pauses the lead's campaigns (logActivity sees inbound: true).
    const ids = await findContactsBy((c) => digits(c.phone) && digits(c.phone) === digits(fromPhone));
    for (const id of ids) await logActivity(id, { type: 'text_reply', inbound: true, channel: 'text', text: `Lead replied by text: ${String(body || '').trim().slice(0, 200)}` }, null);
    return { optedOut: false, contacts: ids };
  }
  const snap = await get(ref(database, 'crm/contacts')); // server/admin only
  const matches = Object.entries(snap.val() || {}).filter(([, c]) => digits(c.phone) && digits(c.phone) === digits(fromPhone));
  await Promise.all(matches.map(([id]) => recordOptOut(id, 'texts', 'Text reply', `Replied "${String(body).trim()}"`)));
  return { optedOut: true, contacts: matches.map(([id]) => id) };
};

// Stub for the future email unsubscribe link.
export const handleEmailUnsubscribe = (contactId, campaignId = '') =>
  recordOptOut(contactId, 'email', 'Email unsubscribe link', campaignId ? `Campaign ${campaignId}` : '');

// ---- Phone numbers ----
// Stored as 10 digits (US); shown as 239-445-4532. phoneE164 gives +12394454532 for dialing/texting.
export const phoneDigits = (p) => {
  let d = String(p || '').replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
  return d.slice(0, 10);
};
export const formatPhoneDash = (p) => {
  const d = phoneDigits(p);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
};
export const phoneE164 = (p) => { const d = phoneDigits(p); return d.length === 10 ? `+1${d}` : ''; };

// Pin / unpin a timeline entry on a contact (editors only).
export const setActivityPin = async (contact, activityId, pinned, uid, isAdmin) => {
  if (!canEditContact(contact, uid, isAdmin)) throw new CrmPermissionError('View only: you cannot edit this contact.');
  await update(ref(database, `crm/activity/${contact.id}/${activityId}`), pinned ? { pinned: true, pinnedAt: now(), pinnedBy: uid || null } : { pinned: null, pinnedAt: null, pinnedBy: null });
  await computeNoteSummary(contact, true);
};
export const deleteActivity = async (contact, activityId, uid, isAdmin) => {
  if (!canEditContact(contact, uid, isAdmin)) throw new CrmPermissionError('View only: you cannot edit this contact.');
  await remove(ref(database, `crm/activity/${contact.id}/${activityId}`));
  await computeNoteSummary(contact, true);
};

// ---------------- Archive / delete / export ----------------
// Archived contacts get no outreach of any kind, manual or automatic. Archiving also stops their campaign
// enrollments. Unarchiving does NOT restart anything, so steps missed while archived are never sent.
export const archiveContact = async (existing, uid, isAdmin) => {
  if (!canEditContact(existing, uid, isAdmin)) throw new CrmPermissionError('You can only archive your own contacts.');
  const at = now();
  await update(ref(database, `crm/contacts/${existing.id}`), { archived: true, archivedAt: at, archivedBy: uid || null, updatedAt: at });
  const enr = await ownedVal('crm/enrollments', uid, isAdmin);
  for (const [id, e] of Object.entries(enr)) {
    if (!e || e.contactId !== existing.id || e.status !== 'active') continue;
    const schedule = (e.schedule || []).map((s) => (s.status === 'scheduled' ? { ...s, status: 'skipped', reason: 'archived' } : s));
    await update(ref(database, `crm/enrollments/${id}`), { status: 'stopped_archived', stoppedAt: at, schedule });
  }
  const msgs = await ownedVal('crm/scheduledMessages', uid, isAdmin);
  for (const [id, m] of Object.entries(msgs)) {
    if (m && m.contactId === existing.id && m.status === 'pending') await update(ref(database, `crm/scheduledMessages/${id}`), { status: 'skipped_archived', skippedAt: at });
  }
  await logActivity(existing.id, { type: 'status', text: 'Archived. All outreach stopped.' }, uid);
};
export const unarchiveContact = async (existing, uid, isAdmin) => {
  if (!canEditContact(existing, uid, isAdmin)) throw new CrmPermissionError('You can only unarchive your own contacts.');
  await update(ref(database, `crm/contacts/${existing.id}`), { archived: false, unarchivedAt: now(), unarchivedBy: uid || null, updatedAt: now() });
  await logActivity(existing.id, { type: 'status', text: 'Unarchived. Nothing missed while archived was sent; re-enroll in campaigns if needed.' }, uid);
};

// Admin only. Permanently removes the contact, its personal lead tasks (any user's tasks linked to it),
// its links on other contacts, and either unlinks or deletes its pipeline records. One contact at a time.
export const deleteContactPermanently = async (existing, { deletePipeline }, uid, isAdmin) => {
  if (!isAdmin) throw new CrmPermissionError('Only an Admin can delete a contact.');
  const id = existing.id; const updates = {};
  for (const path of ['crm/sellerInterests', 'crm/buyerInterests']) {
    const rows = (await get(ref(database, path))).val() || {};
    const linkKey = path.endsWith('sellerInterests') ? 'sellerInterestIds' : 'buyerInterestIds';
    for (const [rid, r] of Object.entries(rows)) {
      if (!r || !(r.contactId === id || (existing[linkKey] || {})[rid])) continue;
      if (deletePipeline) updates[`${path}/${rid}`] = null; else updates[`${path}/${rid}/contactId`] = null;
    }
  }
  const users = (await get(ref(database, 'users'))).val() || {};
  for (const [u, rec] of Object.entries(users)) {
    for (const [tid, t] of Object.entries((rec && rec.tasks) || {})) if (t && t.crmContactId === id) updates[`users/${u}/tasks/${tid}`] = null;
  }
  for (const other of Object.keys(existing.linkedContactIds || {})) updates[`crm/contacts/${other}/linkedContactIds/${id}`] = null;
  const enr = (await get(ref(database, 'crm/enrollments'))).val() || {};
  for (const [eid, e] of Object.entries(enr)) if (e && e.contactId === id) updates[`crm/enrollments/${eid}`] = null;
  const msgs = (await get(ref(database, 'crm/scheduledMessages'))).val() || {};
  for (const [mid, m] of Object.entries(msgs)) if (m && m.contactId === id && m.status === 'pending') updates[`crm/scheduledMessages/${mid}/status`] = 'skipped_deleted';
  updates[`crm/contacts/${id}`] = null;
  Object.assign(updates, indexOps('contacts', id, {}, existing)); // drop everyone's list entry
  await update(ref(database), updates);
};

// Contact export (.xlsx) is limited to these roles; checked again where the file is built.
export const EXPORT_ROLES = ['Admin', 'TC', 'Marketing Manager'];
export const canExportContacts = (role) => EXPORT_ROLES.includes(normalizeRole(role));
